import React, { useId } from 'react';

export interface ThermalRelaySvgProps {
  designator: string;
  isTripped?: boolean;
  currentSettingAmps?: number;
  onTripToggle?: () => void;
  terminalValues?: Record<string, string>;
  highlightTerminals?: string[];
  activeTerminalId?: string | null | undefined;
  onTerminalClick?: (terminalId: string, e: React.MouseEvent) => void;
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Endüstriyel 3 Kutuplu Termik Aşırı Akım Rölesi (Thermal Overload Relay) Vektörel Çizimi
 * 95-96 NC (Devre koruma), 97-98 NO (Arıza ihbar) ve 2-4-6 motor çıkış klemensli.
 * Test ve Reset butonları ile arıza simüle edilebilir.
 */
export const ThermalRelaySvg: React.FC<ThermalRelaySvgProps> = ({
  designator = 'FR1',
  isTripped = false,
  currentSettingAmps = 5.0,
  onTripToggle,
  terminalValues = {},
  highlightTerminals = [],
  activeTerminalId = null,
  onTerminalClick,
  style,
  className = '',
}) => {
  const rawId = useId();
  const uid = rawId.replace(/[:]/g, '_');

  const width = 96;
  const height = 95;

  const getTerminalColor = (tid: string): string => {
    const pot = terminalValues[tid];
    if (pot === 'L1') return '#f59e0b';
    if (pot === 'L2') return '#ea580c';
    if (pot === 'L3') return '#cbd5e1';
    if (pot === 'N') return '#38bdf8';
    if (pot === 'PE') return '#84cc16';
    return '#94a3b8';
  };

  const renderScrewTerminal = (tid: string, label: string, cx: number, cy: number, isAux = false) => {
    const isTarget = highlightTerminals.includes(tid);
    const isActive = activeTerminalId === tid;
    const termColor = getTerminalColor(tid);
    const hasPotential = terminalValues[tid] && terminalValues[tid] !== 'FLOATING';

    return (
      <g
        key={tid}
        transform={`translate(${cx}, ${cy})`}
        style={{ cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation();
          onTerminalClick?.(tid, e);
        }}
      >
        <circle cx="0" cy="0" r={isAux ? '5.5' : '6.5'} fill="#18202c" stroke="#334155" strokeWidth="1" />
        <circle cx="0" cy="0" r={isAux ? '3.5' : '4.2'} fill="#64748b" stroke="#0f172a" strokeWidth="0.8" />
        <line x1="-2.5" y1="-1.2" x2="2.5" y2="1.2" stroke="#0f172a" strokeWidth="1.2" />

        {hasPotential && (
          <circle cx="0" cy="0" r="7" fill="none" stroke={termColor} strokeWidth="1.5" strokeOpacity="0.8" />
        )}

        {(isTarget || isActive) && (
          <circle
            cx="0"
            cy="0"
            r="9.5"
            fill="none"
            stroke="#38bdf8"
            strokeWidth="1.8"
            strokeDasharray={isActive ? 'none' : '3,2'}
            className="terminal-pulse"
          />
        )}

        <text
          x="0"
          y={cy < 40 ? -7 : 12}
          fill="#cbd5e1"
          fontSize="6.5"
          fontFamily="system-ui, monospace"
          fontWeight="bold"
          textAnchor="middle"
        >
          {label}
        </text>
      </g>
    );
  };

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={`thermal-relay-device-svg ${className}`}
      style={{ overflow: 'visible', userSelect: 'none', display: 'block', ...style }}
    >
      <defs>
        <linearGradient id={`thermalBodyGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#475569" />
          <stop offset="30%" stopColor="#334155" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>

        <filter id={`thermalShadow-${uid}`} x="-15%" y="-15%" width="130%" height="130%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000000" floodOpacity="0.4" />
        </filter>
      </defs>

      {/* Gövde */}
      <g filter={`url(#thermalShadow-${uid})`}>
        <rect
          x="1"
          y="1"
          width={width - 2}
          height={height - 2}
          rx="5"
          fill={`url(#thermalBodyGrad-${uid})`}
          stroke="#64748b"
          strokeWidth="1.2"
        />

        {/* Cihaz Başlığı */}
        <text
          x="14"
          y="15"
          fill="#38bdf8"
          fontSize="7.5"
          fontFamily="system-ui, sans-serif"
          fontWeight="bold"
        >
          {designator}
        </text>

        <text
          x={width - 12}
          y="15"
          fill="#94a3b8"
          fontSize="6"
          fontFamily="system-ui, sans-serif"
          textAnchor="end"
        >
          THERMAL O/L
        </text>

        {/* Akım Ayar Kadranı (Current Dial) */}
        <g transform="translate(24, 38)">
          <circle cx="0" cy="0" r="12" fill="#1e293b" stroke="#64748b" strokeWidth="1" />
          <circle cx="0" cy="0" r="9" fill="#0f172a" />
          <line x1="0" y1="0" x2="6" y2="-4" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" />
          <text x="0" y="18" fill="#f8fafc" fontSize="6" fontFamily="monospace" fontWeight="bold" textAnchor="middle">
            {currentSettingAmps.toFixed(1)}A
          </text>
        </g>

        {/* Manuel/Otomatik Reset & Test Butonları */}
        <g
          transform="translate(68, 38)"
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            onTripToggle?.();
          }}
        >
          {/* Mavi Reset Butonu */}
          <rect x="-16" y="-8" width="14" height="14" rx="2" fill="#0284c7" stroke="#0369a1" strokeWidth="0.8" />
          <text x="-9" y="2" fill="#ffffff" fontSize="5" fontFamily="system-ui, sans-serif" fontWeight="bold" textAnchor="middle">
            RESET
          </text>

          {/* Kırmızı Test/Trip Butonu */}
          <rect x="2" y="-8" width="14" height="14" rx="2" fill="#dc2626" stroke="#991b1b" strokeWidth="0.8" />
          <text x="9" y="2" fill="#ffffff" fontSize="5" fontFamily="system-ui, sans-serif" fontWeight="bold" textAnchor="middle">
            TEST
          </text>

          {/* Arıza Durum Bayrağı */}
          <circle
            cx="9"
            cy="11"
            r="3.5"
            fill={isTripped ? '#ef4444' : '#22c55e'}
            stroke="#0f172a"
            strokeWidth="0.8"
          />
          <text x="16" y="13" fill="#94a3b8" fontSize="5" fontFamily="system-ui, sans-serif">
            {isTripped ? 'TRIP' : 'OK'}
          </text>
        </g>
      </g>

      {/* Yardımcı Kontaklar (Orta-Alt Katman: 95-96 NC ve 97-98 NO) */}
      {renderScrewTerminal('95', '95 NC', 14, 60, true)}
      {renderScrewTerminal('96', '96 NC', 30, 60, true)}
      {renderScrewTerminal('97', '97 NO', 66, 60, true)}
      {renderScrewTerminal('98', '98 NO', 82, 60, true)}

      {/* Ana Güç Çıkış Terminalleri (Alt Sıra: 2/T1, 4/T2, 6/T3 - Motora gider) */}
      {renderScrewTerminal('2', '2/T1', 24, 82)}
      {renderScrewTerminal('4', '4/T2', 48, 82)}
      {renderScrewTerminal('6', '6/T3', 72, 82)}
    </svg>
  );
};
